import React from 'react';

import type {
  ControllerProps,
  FieldPath,
  FieldValues,
  PathValue,
} from 'react-hook-form';
import { Controller } from 'react-hook-form';

import type { OutlinedInputProps } from '@packages/fenrir/components';
import { OutlinedInput, FormControlWrapper } from '@packages/fenrir/components';

export type ValueTransform<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  input: (value: PathValue<TFieldValues, TName>) => unknown;
  output: (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => PathValue<TFieldValues, TName>;
};

export type ControlledTextFieldV2Props<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  controllerProps: Omit<
    ControllerProps<TFieldValues, TName>,
    'render' | 'children'
  >;
  transform?: ValueTransform<TFieldValues, TName>;
  outlinedInputProps?: Omit<
    OutlinedInputProps,
    'input' | 'onChange' | 'defaultValue'
  >;
} & Omit<
  React.ComponentProps<typeof FormControlWrapper>,
  'error' | 'children' | 'defaultValue'
>;

export function ControlledTextFieldV2<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  controllerProps,
  helperText,
  label,
  outlinedInputProps,
  transform,
  ...formControlWrapperProps
}: ControlledTextFieldV2Props<TFieldValues, TName>) {
  return (
    <Controller
      {...controllerProps}
      render={({ field, fieldState }) => (
        <FormControlWrapper
          label={label}
          helperText={fieldState.error?.message ?? helperText}
          error={Boolean(fieldState.error)}
          inputId={outlinedInputProps?.inputProps?.id}
          {...formControlWrapperProps}
        >
          <OutlinedInput
            {...field}
            value={transform?.input(field.value) ?? field.value}
            onChange={(event) =>
              field.onChange(transform?.output(event) ?? event.target.value)
            }
            {...outlinedInputProps}
            inputProps={{
              'aria-label': label,
              ...outlinedInputProps?.inputProps,
            }}
          />
        </FormControlWrapper>
      )}
    />
  );
}
